import { DateTime } from 'luxon';
import { ANDROID_CONNECTIVITY_LOST_PHRASES, IOS_CONNECTIVITY_LOST_PHRASES } from './error-patterns';

// Pure helpers for the sync false-positive diagnostics fix (Sentry triage v1.25.0 Group 1:
// issues 429 "stale unflushed local change" and 3ZD "SyncCheckpointDrift"). They live in
// this separate module because the DatabaseWrapper class can't be loaded under node (its
// top-level pouchdb-browser import references `self`), so the logic-heavy parts are extracted
// here and unit-tested directly. DatabaseWrapper delegates to them.
//
// What lives here is the failure side: the shape of the last sync error, what counts as a
// network-shaped failure, and how a failed heal attempt is classified.

/** The shape DatabaseWrapper stores for the most recent sync error. */
export interface LastSyncError {
  source: string;
  error: any;
  timestamp: string;
}

// network-shaped error strings, beyond the native phrase lists imported above:
//   iOS WebKit / WKWebView: "load failed", plus the broader "connection was lost" and
//     "network connection", which also catch wordings the native list does not name exactly
//   fetch layer: "networkerror", "failed to fetch", "fetch is aborted", "aborterror"
//   timeouts: "timed out", "timeout" (sync treats a timeout as network-shaped, while
//     error-patterns.ts's native lists deliberately leave timeouts out)
const SYNC_ONLY_NETWORK_PHRASES: readonly string[] = [
  'load failed', 'connection was lost', 'network connection',
  'networkerror', 'failed to fetch', 'fetch is aborted', 'aborterror',
  'timed out', 'timeout',
];

/** escape a literal phrase so it matches itself inside a RegExp */
function escapeForRegExp(phrase: string): string {
  return phrase.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
}

// the native iOS and Android phrases come from error-patterns.ts (#4010, #6086), so a phrase
// added there is recognized here too; before, this was a hand-kept copy that had drifted.
// shared by isRecentNetworkSyncError and classifySyncFailure so the two agree on what
// counts as a network-shaped failure. Messages are lowercased before they are tested.
const NETWORK_SYNC_ERROR_SHAPE = new RegExp(
  [...IOS_CONNECTIVITY_LOST_PHRASES, ...ANDROID_CONNECTIVITY_LOST_PHRASES, ...SYNC_ONLY_NETWORK_PHRASES]
    .map(escapeForRegExp)
    .join('|'),
);

/**
 * True when the most recent sync error looks like a transient network failure and is recent
 * enough to explain a current pause. On iOS the network can drop or be suspended while
 * connectionState still reads online (it only flips via a `ping.svg` probe), so connectionState
 * alone misses these — a fresh network-shaped sync error does not. Used to gate the
 * stale-unflushed-change (429) and checkpoint-drift (3ZD) diagnostics so a known network drop
 * is not mislabeled as a silent stall / data-loss risk.
 */
export function isRecentNetworkSyncError(
  lastError: LastSyncError | null | undefined,
  nowMs: number,
  withinMs = 30_000,
): boolean {
  if (!lastError) return false;
  const ageMs = nowMs - DateTime.fromISO(lastError.timestamp).toMillis();
  if (ageMs > withinMs) return false;
  // a foreground fetch timeout is recorded with source 'timeout'
  if (lastError.source === 'timeout') return true;
  return isNetworkShapedSyncError(lastError.error);
}

/**
 * True when an error's name or message looks like a transient network failure: the
 * connection dropped, the request timed out, or the fetch was aborted. The live-sync
 * `paused` handler uses this to record every such pause, not only the exact iOS
 * "Load failed" wording (df-280a5f714662: on iOS resume the hung request fails with
 * "The network connection was lost." or "The request timed out.").
 */
export function isNetworkShapedSyncError(err: { name?: string, message?: string } | null | undefined): boolean {
  const message = `${err?.name ?? ''} ${err?.message ?? ''}`.toLowerCase();
  return NETWORK_SYNC_ERROR_SHAPE.test(message);
}

/** the documented contract of {@link classifySyncFailure}, encoded so a typo'd class at a
 *  call site fails to compile instead of silently mis-grouping telemetry */
export type SyncFailureClass = `http-${number}` | 'network' | 'other';

/**
 * Classify a failed force-push heal attempt so the SyncCheckpointDrift report carries WHY the
 * heal PUT failed, not just that it did (#4010 B4 evidence logging). Three classes:
 * - `http-<status>` — the server answered with an HTTP error (a real, addressable failure);
 * - `network` — the request never completed (the same network shapes the transient gate
 *   recognizes, so "network" here corroborates the residual-is-transient theory);
 * - `other` — anything else (unexpected; worth a look when it shows up in numbers).
 */
export function classifySyncFailure(err: { name?: string, message?: string, status?: number } | null | undefined): SyncFailureClass {
  if (typeof err?.status === 'number') return `http-${err.status}`;
  const message = `${err?.name ?? ''} ${err?.message ?? ''}`.toLowerCase();
  if (NETWORK_SYNC_ERROR_SHAPE.test(message)) return 'network';
  return 'other';
}
